import React, { useRef, useState } from "react";
import ActionsBar from "components/SubHeader/ActionsBar";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { TCity, TJurisdiction, TWaterMeter } from "types/dto";
import { toast } from "react-toastify";
import { AddMeterModal, TAddFormTypes } from "./AddMeterModal";
import { EditMeterModal, TEditFormTypes } from "./EditMeterModal";
import { DataGridRef } from "devextreme-react/data-grid";
import WaterMetersGrid from "components/Grid/grids/WaterMetersGrid";
import { useURLTools } from "utils/URLtools";
import { TWaterMetersGridRow } from "components/Grid/grids/WaterMetersGrid/types";
import { TResponseCode } from "utils/netCall/types";
import { usePermissions } from "components/Permission/usePermissions";

const MetersView = () => {
  const { t } = useTranslation();
  const { navigateWithQueryParams } = useURLTools();
  const { hasPermission } = usePermissions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TJurisdiction>();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const GridRef = useRef<DataGridRef<any, any>>(null);

  const onAddClick = () => {
    setAddModalOpen(true);
  };
  const onEditClick = (data: any) => {
    if (!hasPermission(["d_watermeter", "superadmin"])) {
      toast.error(t("youDontHavePermissionToEdit"));
      return;
    }
    setSelectedRow(data);
    setIsEditModalOpen(true);
  };

  const onSubmitAddMeter = async (values: TAddFormTypes) => {
    console.log("VALID", values);
    setIsSubmitting(true);
    const res = await netCall<TWaterMeter>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterMeter/AddWaterMeter`,
      body: {
        ...values,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setIsSubmitting(false);
      toast.success(t("operationSuccessful"));
      setAddModalOpen(false);
      GridRef?.current?.instance().refresh();
    } else {
      setIsSubmitting(false);
      toast.error(t("operationFailed"));
    }
  };
  const onSubmitEditMeter = async (values: TEditFormTypes) => {
    if (selectedRow) {
      setIsSubmitting(true);
      const res = await netCall<TCity>({
        method: "POST",
        endPoint: `${ManagementApiUrl()}/api/WaterMeter/UpdateWaterMeter`,
        body: {
          id: selectedRow?.id,
          ...values,
        },
      });
      if (res.responseCode === TResponseCode.OK) {
        setIsSubmitting(false);
        toast.success(t("operationSuccessful"));
        GridRef.current?.instance().refresh();
        setIsEditModalOpen(false);
      } else {
        setIsSubmitting(false);
        toast.error(t("operationFailed"));
      }
    }
  };

  const onWaterMetersRowClick = (row: TWaterMetersGridRow) => {
    navigateWithQueryParams(`/dashboard/meters/details/${row.id}`, [], {
      replace: true,
    });
  };

  return (
    <>
      <SubHeader>
        <SubHeaderTitle>{t("meters")}</SubHeaderTitle>
        <ActionsBar
          requestTime={0}
          isLoading={false}
          actionButtons={[
            {
              iconName: "MdAdd",
              buttonTooltip: "افزودن",
              action: onAddClick,
              permissions: ["d_watermeter", "superadmin"],
            },
          ]}
        />
      </SubHeader>
      <div className={"w-full flex gap-x h-[calc(100%-56px)]"}>
        <WaterMetersGrid
          enableDetailsContextMenu
          actionButtons={[
            {
              icon: "fa-solid fa-pencil",
              hint: t("edit"),
              onClick: onEditClick,
            },
          ]}
          isLoading={false}
          ref={GridRef}
          onRowClick={onWaterMetersRowClick}
        />
      </div>
      {addModalOpen && (
        <AddMeterModal
          onClose={() => setAddModalOpen(false)}
          isOpen={addModalOpen}
          onSubmit={onSubmitAddMeter}
          isLoading={isSubmitting}
        />
      )}
      {isEditModalOpen && (
        <EditMeterModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRow(undefined);
          }}
          onSubmit={onSubmitEditMeter}
          isLoading={isSubmitting}
          waterMeterId={selectedRow?.id || ""}
        />
      )}
    </>
  );
};

export default MetersView;
